import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  Grid,
  Save,
  Check,
  Edit,
  Plus,
  Trash2,
  Layers,
  FolderCog,
  Gauge,
  ArrowLeft,
  GalleryHorizontalEnd,
  Eye,
  EyeOff,
} from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';
import { buildTenantStorageKey, readTenantStorageItem, writeTenantStorageItem } from '../../lib/storage/tenantStorage';

export interface LayoutPresetsMenuProps {
  onSave?: () => void;
  onSaveConfirmation?: (message: string) => void;
  onManageWidgets?: () => void;
  onGridChange?: (cols: number) => void;
  dashboardUserKey?: string;
  tenantSlug?: string | null;
  cols?: number;
  className?: string;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
}

type Preset = {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  isDefault: boolean;
  layout?: any;
};

const STORAGE_KEY = buildTenantStorageKey('dashboard:custom-presets-v1');
const STORAGE_KEY_LEGACY = 'dashboard:custom-presets-v1';
const ACTIVE_PRESET_KEY_BASE = 'dashboard:active-layout-preset';
const ACTIVE_PRESET_KEY_LEGACY = 'dashboard:active-layout-preset';

// Simplified dummy data for demonstration
const mockPresets: Preset[] = [
  {
    id: 'performance',
    name: 'Performance',
    icon: Gauge,
    isDefault: true,
  },
  {
      id: 'trades',
      name: 'Trades',
      icon: Layers,
      isDefault: true,
    },
    {
      id: 'patterns',
      name: 'Patterns',
      icon: Grid,
      isDefault: true,
    },
    {
      id: 'complete',
      name: 'Complete',
      icon: GalleryHorizontalEnd,
      isDefault: true,
    }
  ];

// Replicated Grid Icon components (needed for the Grid Options view)
const GridIcon2: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M12 3v18" />
  </svg>
);

const GridIcon3: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="M15 3v18" />
  </svg>
);

const LayoutPresetsMenu: React.FC<LayoutPresetsMenuProps> = ({ onSave, onManageWidgets, onGridChange, cols, className = '', menuButtonRef, dashboardUserKey, tenantSlug }) => {
  const userScope = (dashboardUserKey || 'guest').trim() || 'guest';
  const ACTIVE_PRESET_KEY = buildTenantStorageKey(ACTIVE_PRESET_KEY_BASE, userScope);
  const loadInitialPresets = (): { presets: Preset[]; activeId: string } => {
    try {
      const raw = readTenantStorageItem(STORAGE_KEY, [STORAGE_KEY_LEGACY]);
      const savedActive = readTenantStorageItem(ACTIVE_PRESET_KEY, [ACTIVE_PRESET_KEY_LEGACY]);
      const parsed = raw ? JSON.parse(raw) : [];
      const custom: Preset[] = Array.isArray(parsed)
        ? parsed.map((p: any) => ({
            id: p.id,
            name: p.name,
            icon: FolderCog,
            isDefault: false,
          }))
        : [];
      const merged = mockPresets.concat(custom);
      const activeId = savedActive && merged.some(p => p.id === savedActive) ? savedActive : 'complete';
      return { presets: merged, activeId };
    } catch {
      return { presets: mockPresets, activeId: 'complete' };
    }
  };

  const initial = loadInitialPresets();

  const [isOpen, setIsOpen] = useState(false);
  const [activeLayoutId, setActiveLayoutId] = useState<string>(initial.activeId);
  const [isCreating, setIsCreating] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [menuView, setMenuView] = useState<'main' | 'grid-options'>('main');
  const [presets, setPresets] = useState<Preset[]>(initial.presets);
  const [presetStatusById, setPresetStatusById] = useState<Record<string, { isBuiltInPreset: boolean; isModified: boolean }>>({});
  const [layoutDebugState, setLayoutDebugState] = useState<any>(null);

  const [observedCols, setObservedCols] = useState<number | undefined>(undefined);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  // Rename/editing state
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const editInputRef = useRef<HTMLInputElement | null>(null);

  // Ref for the open dropdown so we can detect click-away
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Track allowed max columns based on current physical screen width (mirrors old LayoutMenu behavior)
  const [maxCols, setMaxCols] = useState<number>(4);
  const isDev = typeof import.meta !== 'undefined'
    ? !!(import.meta as any).env?.DEV
    : ((globalThis as any).process?.env?.NODE_ENV === 'development');

  const showToast = (text: string, variant: 'box' | 'inline' = 'box') => {
    try {
      window.dispatchEvent(new CustomEvent('dashboard:toolbar-confirmation', { detail: { message: text, variant } }));
    } catch {}
  };

  useEffect(() => {
    if (!isOpen) {
      const timer = window.setTimeout(() => setMenuView('main'), 300);
      return () => window.clearTimeout(timer);
    }
  }, [isOpen]);

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      try {
        const target = e.target;
        if (!menuRef.current) return;
        if (!(target instanceof Node)) return;
        if (!menuRef.current.contains(target)) {
          closeAll();
        }
      } catch {
        // ignore
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Listen to dashboard open/close preset menu events for onboarding tour integration
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);
    window.addEventListener('dashboard:open-preset-menu', onOpen);
    window.addEventListener('dashboard:close-preset-menu', onClose);
    return () => {
      window.removeEventListener('dashboard:open-preset-menu', onOpen);
      window.removeEventListener('dashboard:close-preset-menu', onClose);
    };
  }, []);

  // Helper to persist custom presets
  const saveCustomPresetsToStorage = (items: Preset[]) => {
    try {
      const custom = items
        .filter(p => !p.isDefault)
        .map(p => ({ id: p.id, name: p.name, layout: p.layout ?? null }));
      writeTenantStorageItem(STORAGE_KEY, JSON.stringify(custom));
    } catch {
      // ignore
    }
  };

  // Persist the active preset selection
  useEffect(() => {
    try {
      if (activeLayoutId) writeTenantStorageItem(ACTIVE_PRESET_KEY, activeLayoutId);
    } catch {
      // ignore
    }
  }, [activeLayoutId]);

  // Notify the dashboard when the active preset changes
  useEffect(() => {
    try {
      if (!activeLayoutId) return;
      window.dispatchEvent(new CustomEvent('dashboard:active-preset-changed', { detail: { presetId: activeLayoutId } }));
    } catch {
      // ignore
    }
  }, [activeLayoutId]);

  // Persist user-created presets (non-default) to localStorage whenever they change
  useEffect(() => {
    saveCustomPresetsToStorage(presets);
  }, [presets]);

  useEffect(() => {
    const onPresetStatus = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail || {};
        const presetId = String(detail.presetId || '').trim();
        if (!presetId) return;
        setPresetStatusById((prev) => ({
          ...prev,
          [presetId]: {
            isBuiltInPreset: !!detail.isBuiltInPreset,
            isModified: !!detail.isModified,
          },
        }));
      } catch {}
    };

    window.addEventListener('dashboard:active-preset-layout-status', onPresetStatus as EventListener);
    return () => window.removeEventListener('dashboard:active-preset-layout-status', onPresetStatus as EventListener);
  }, []);

  useEffect(() => {
    const onLayoutDebugState = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail || null;
        setLayoutDebugState(detail);
      } catch {}
    };

    window.addEventListener('dashboard:layout-debug-state', onLayoutDebugState as EventListener);
    return () => window.removeEventListener('dashboard:layout-debug-state', onLayoutDebugState as EventListener);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.screen === 'undefined') return;

    const getScreenWidth = () => {
      const reported = window.screen?.availWidth ?? window.screen?.width ?? 0;
      const dpr = window.devicePixelRatio || 1;
      return Math.round(reported * dpr);
    };

    const update = () => {
      const phys = getScreenWidth();
      if (phys > 2560) setMaxCols(5);
      else if (phys >= 2560) setMaxCols(4);
      else setMaxCols(3);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  // Clamp persisted cols if the screen can't support it (keeps old "Grid" behavior)
  useEffect(() => {
    if (typeof cols !== 'number') return;
    if (typeof onGridChange !== 'function') return;
    if (cols > maxCols) {
      try {
        onGridChange(maxCols);
      } catch {}
    }
  }, [cols, maxCols, onGridChange]);

  // If cols is not provided, observe dashboard grid changes via a custom event.
  useEffect(() => {
    const onCols = (e: Event) => {
      try {
        const next = (e as CustomEvent).detail?.cols;
        if (typeof next !== 'number') return;
        setObservedCols(next);
      } catch {}
    };
    window.addEventListener('dashboard:cols-changed', onCols as EventListener);
    return () => window.removeEventListener('dashboard:cols-changed', onCols as EventListener);
  }, []);

  const closeAll = () => {
    setIsOpen(false);
    setIsCreating(false);
    setNewLayoutName('');
    setMenuView('main');
  };

  const handleSave = () => {
    if (saving) return;
    setSaving(true);
    try {
      if (typeof onSave === 'function') {
        onSave();
      } else {
        window.dispatchEvent(new CustomEvent('dashboard:request-save-layout'));
      }
    } catch {}

    showToast('Layout Saved.', 'inline');
    closeAll();
    setSaving(false);
  };

  const handleGridChange = (cols: number) => {
    try {
      if (typeof onGridChange === 'function') {
        onGridChange(cols);
      } else {
        window.dispatchEvent(new CustomEvent('dashboard:request-grid-change', { detail: { cols } }));
        setObservedCols(cols);
      }
    } catch {}
    showToast(`Grid: ${cols} columns`, 'inline');
    closeAll();
  };

  const effectiveCols = typeof cols === 'number' ? cols : observedCols;
  const gridOptions = [2, 3, 4, 5].filter((n) => n <= maxCols);

  const GridIconColumns: React.FC<{ columns: number; className?: string }> = ({ columns, className }) => {
    const lines = Math.max(1, columns - 1);
    const startX = 6;
    const endX = 18;
    const step = lines > 0 ? (endX - startX) / (lines + 1) : 0;
    const xs = Array.from({ length: lines }, (_, i) => Math.round(startX + step * (i + 1)));

    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        {xs.map((x) => (
          <path key={x} d={`M${x} 3v18`} />
        ))}
      </svg>
    );
  };

  const handleCreate = () => {
    if (!newLayoutName.trim()) return;
    const newPreset: Preset = {
      id: `custom-${Date.now()}`,
      name: newLayoutName,
      // Use gallery-vertical-end icon for user-created layouts
      icon: FolderCog,
      isDefault: false,
    };
    setPresets((prev) => {
      const next = [...prev, newPreset];
      // persist immediately
      saveCustomPresetsToStorage(next);
      return next;
    });
    setActiveLayoutId(newPreset.id);
    setNewLayoutName('');
    setIsCreating(false);
  };

  const startRename = (presetId: string, name: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingPresetId(presetId);
    setEditingName(name);
  };

  const commitRename = (presetId: string | null) => {
    if (!presetId) {
      setEditingPresetId(null);
      setEditingName('');
      return;
    }
    const trimmed = editingName.trim();
    if (!trimmed) return;
    setPresets((prev) => {
      const next = prev.map(p => p.id === presetId ? { ...p, name: trimmed } : p);
      try { saveCustomPresetsToStorage(next); } catch {}
      return next;
    });
    setEditingPresetId(null);
    setEditingName('');
  };

  useEffect(() => {
    if (editingPresetId && editInputRef.current) {
      try { editInputRef.current.focus(); editInputRef.current.select(); } catch {}
    }
  }, [editingPresetId]);

  const deletePreset = (id: string) => {
    if (activeLayoutId === id) {
      setActiveLayoutId('performance');
    }
    setPresets((prev) => {
      const next = prev.filter((p) => p.id !== id);
      saveCustomPresetsToStorage(next);
      return next;
    });
  };

  const activePreset = presets.find((p) => p.id === activeLayoutId) || presets[0];
  const ActiveIcon = activePreset.icon;
  const activePresetStatus = presetStatusById[activeLayoutId];
  const showResetButton = !!activePreset?.isDefault && !!activePresetStatus?.isBuiltInPreset && !!activePresetStatus?.isModified;
  const showPreviewButton = !!activePreset?.isDefault;

  const renderLayoutSummary = (label: string, layout: any) => {
    if (!layout) return null;
    return (
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-500">{label}</div>
        <div className="space-y-1">
          {Array.isArray(layout.sections) && layout.sections.length ? layout.sections.map((section: any, index: number) => (
            <div key={`${label}-${section?.id ?? index}`} className="rounded-md bg-zinc-100/80 px-2 py-1 text-[11px] text-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300">
              <div className="font-medium text-zinc-900 dark:text-white">{section?.title || 'Group'}</div>
              <div className="text-zinc-500 dark:text-zinc-500">{Array.isArray(section?.widgetIds) ? section.widgetIds.join(', ') || 'No widgets' : 'No widgets'}</div>
            </div>
          )) : (
            <div className="rounded-md bg-zinc-100/80 px-2 py-1 text-[11px] text-zinc-500 dark:bg-zinc-800/80 dark:text-zinc-500">No sections</div>
          )}
        </div>
      </div>
    );
  };

  const requestPresetReset = (presetId: string) => {
    try {
      window.dispatchEvent(new CustomEvent('dashboard:request-preset-reset', { detail: { presetId } }));
    } catch {}
  };
  const [isSideMenuView, setIsSideMenuView] = useState<boolean>(() => {
    try {
      return typeof document !== 'undefined' && !!document.querySelector('.sidebar-root');
    } catch { return false; }
  });

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try { return typeof document !== 'undefined' && document.documentElement.classList.contains('dark'); } catch { return false; }
  });

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const wrapperClass = isSideMenuView
    ? isCollapsed
      ? 'fixed right-2 top-6 z-[10002]'
      : 'fixed right-4 top-6 z-[10002]'
    : 'relative';

  const innerButtonClass = `p-2 rounded shadow-sm transition-colors bg-blue-500 text-white dark:bg-blue-500/30 dark:text-white ${
    isCollapsed && isSideMenuView && !isDarkMode ? 'ring-2 ring-white' : ''
  }`;

  useEffect(() => {
    if (!isSideMenuView || typeof window === 'undefined') return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        setIsCollapsed(y > 80);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // initialize
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isSideMenuView]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const checkSidebar = () => setIsSideMenuView(!!document.querySelector('.sidebar-root'));
    const bodyObserver = new MutationObserver(() => checkSidebar());
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    const el = document.documentElement;
    const classObserver = new MutationObserver(() => setIsDarkMode(el.classList.contains('dark')));
    classObserver.observe(el, { attributes: true, attributeFilter: ['class'] });

    return () => {
      try { bodyObserver.disconnect(); classObserver.disconnect(); } catch {}
    };
  }, []);

  return (
    <div className={`${wrapperClass} font-sans ${className}`}>
      <ConfirmationModal
        isOpen={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => {
          if (!deleteTargetId) return;
          deletePreset(deleteTargetId);
          setDeleteTargetId(null);
        }}
        title="Delete Layout?"
        message="This will permanently remove this custom view."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <button
        ref={menuButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        data-tour-target="dashboard-preset-menu"
        className={(() => {
          const base = `flex items-center p-1 rounded-lg transition-all duration-200 group ${tenantSlug === 'stock-navigators' ? 'border-[0.5px] border-[rgba(59,130,246,0.3)]' : ''}`;
          const collapsedCls = isCollapsed && isSideMenuView ? 'p-0 w-10 h-10 rounded-full justify-center' : '';
          const sideLightBorder = isSideMenuView && !isDarkMode ? 'border-blue-500 border' : '';
          const activeGlow = '';
          const activeBorder = '';
          const surfaceBorder = tenantSlug === 'stock-navigators' ? '' : 'border border-[rgba(59,130,246,0.3)] hover:border-zinc-300 dark:hover:border-zinc-500';
          if (isOpen) return `${base} ${collapsedCls} ${activeGlow} ${activeBorder} ${sideLightBorder || 'bg-white dark:bg-zinc-800'}`;
          return `${base} ${collapsedCls} ${activeGlow} ${activeBorder} ${sideLightBorder || `bg-white dark:bg-zinc-900 ${surfaceBorder}`}`;
        })()}
      >
        <div className={innerButtonClass}>
          <ActiveIcon className="w-4 h-4" />
        </div>
        <div className={`${isCollapsed && isSideMenuView ? 'hidden' : 'px-3'} text-[10pt] uppercase font-medium text-zinc-700 group-hover:text-zinc-900 transition-colors dark:text-zinc-300 dark:group-hover:text-white`}>{activePreset.name}</div>
        <div className={`${isCollapsed && isSideMenuView ? 'hidden' : 'pr-2'} text-zinc-500 group-hover:text-zinc-900 transition-colors dark:group-hover:text-white`}>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9999]" onClick={() => closeAll()} />
          <div
            ref={menuRef}
            className="absolute top-full right-0 mt-2 w-72 bg-white border border-zinc-200 rounded-xl shadow-2xl z-[10000] overflow-visible animate-in fade-in zoom-in-95 duration-100 dark:bg-zinc-900 dark:border-zinc-700 isolate backdrop-blur-none bg-clip-border"
          >
            {menuView === 'main' ? (
              <div data-tour-target="dashboard-customization-controls" className="p-2 grid grid-cols-2 gap-2 border-b border-zinc-200 animate-in slide-in-from-left-2 duration-200 dark:border-gray-800">
                <button
                  onClick={handleSave}
                  disabled={!activePreset || saving}
                  data-tour-target="dashboard-save-layout"
                  className={`flex items-center justify-center gap-2 p-2 rounded text-sm transition-colors ${
                    !activePreset || saving
                      ? 'bg-zinc-50 text-zinc-400 cursor-not-allowed dark:bg-zinc-800/50 dark:text-zinc-500'
                      : 'bg-zinc-100 hover:bg-zinc-200 hover:text-emerald-600 text-zinc-700 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:hover:text-emerald-400 dark:text-zinc-300'
                  }`}
                >
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setMenuView('grid-options')}
                  data-tour-target="dashboard-edit-grid"
                  className="flex items-center justify-center gap-2 p-2 rounded bg-zinc-100 hover:bg-zinc-200 hover:text-blue-600 text-sm text-zinc-700 transition-colors dark:bg-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-blue-400 dark:text-zinc-300"
                >
                  <Grid className="w-3.5 h-3.5" /> Edit Grid
                </button>
                <button
                  onClick={() => {
                    // Close this menu first, then open the WidgetsLauncher on the next tick
                    // to avoid the click event immediately triggering the launcher's click-away.
                    closeAll();
                    try {
                      setTimeout(() => {
                        if (typeof onManageWidgets === 'function') {
                          onManageWidgets();
                        } else {
                          window.dispatchEvent(new CustomEvent('dashboard:request-manage-widgets'));
                        }
                      }, 0);
                    } catch {}
                  }}
                  data-tour-target="dashboard-manage-widgets"
                  className="col-span-2 flex items-center justify-center gap-2 p-2 rounded bg-zinc-100 hover:bg-zinc-200 hover:text-purple-600 text-sm text-zinc-700 transition-colors dark:bg-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-purple-400 dark:text-zinc-300"
                >
                  <Layers className="w-3.5 h-3.5" /> Manage Widgets
                </button>
                {showPreviewButton && (
                  <button
                    onClick={() => {
                      try {
                        window.dispatchEvent(new CustomEvent('dashboard:request-preview-default', { detail: { presetId: activeLayoutId } }));
                      } catch {}
                    }}
                    data-tour-target="dashboard-preview-default"
                    className="col-span-2 flex items-center justify-center gap-2 p-2 rounded bg-zinc-100 hover:bg-zinc-200 hover:text-amber-600 text-sm text-zinc-700 transition-colors dark:bg-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-amber-400 dark:text-zinc-300"
                  >
                    {layoutDebugState?.isPreviewingDefault ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {layoutDebugState?.isPreviewingDefault ? 'Restore Saved' : 'Preview Default'}
                  </button>
                )}
              </div>
            ) : (
              <div className="p-2 border-b border-zinc-200 animate-in slide-in-from-right-2 duration-200 dark:border-gray-800">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <button
                    onClick={() => setMenuView('main')}
                    className="p-1 -ml-1 hover:bg-zinc-100 rounded text-zinc-500 hover:text-zinc-900 transition-colors dark:hover:bg-zinc-800 dark:hover:text-white"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider dark:text-gray-500">Select Columns</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {gridOptions.map((n) => {
                    const active = typeof effectiveCols === 'number' && effectiveCols === n;
                    const Icon = n === 2 ? GridIcon2 : n === 3 ? GridIcon3 : null;
                    return (
                      <button
                        key={n}
                        onClick={() => handleGridChange(n)}
                        className={`flex flex-col items-center justify-center gap-1 p-2 rounded bg-zinc-100 hover:bg-blue-600/10 hover:text-blue-600 border text-xs transition-all group/col dark:bg-zinc-800 dark:hover:bg-blue-600/20 dark:hover:text-blue-500 ${
                          active
                                ? 'border-blue-600/40 text-blue-600 ring-1 ring-blue-500/60 shadow-[0_0_0_1px_rgba(59,130,246,0.18),0_0_14px_rgba(59,130,246,0.16)] dark:border-blue-500/40 dark:text-blue-400'
                            : 'border-transparent text-zinc-500 hover:border-blue-600/30 dark:text-gray-400 dark:hover:border-blue-500/30'
                        }`}
                      >
                        {Icon ? (
                          <Icon className={`w-4 h-4 ${active ? 'opacity-100' : 'opacity-50 group-hover/col:opacity-100'}`} />
                        ) : (
                          <GridIconColumns
                            columns={n}
                            className={`w-4 h-4 ${active ? 'opacity-100' : 'opacity-50 group-hover/col:opacity-100'}`}
                          />
                        )}
                        <span className="font-medium">{n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="p-2">
              <div className="px-2 py-1 text-sm font-semibold text-center text-zinc-800 uppercase tracking-wider dark:text-gray-400">Layout</div>
              <div className="mt-0 mb-3 h-px bg-zinc-200 dark:bg-gray-800" />
              <div data-tour-target="dashboard-preset-list">
                {presets.map((preset) => (
                <div key={preset.id} className="relative group/item">
                  <button
                    onClick={() => {
                      setActiveLayoutId(preset.id);
                      closeAll();
                    }}
                    data-tour-target={
                      preset.id === 'performance'
                        ? 'dashboard-preset-performance'
                        : preset.id === 'trades'
                        ? 'dashboard-preset-trades'
                        : preset.id === 'patterns'
                        ? 'dashboard-preset-patterns'
                        : preset.id === 'complete'
                        ? 'dashboard-preset-complete'
                        : undefined
                    }
                    className={`
                        w-full flex items-center justify-between px-3 py-2.5 pr-24 rounded-lg text-sm transition-all
                      ${activeLayoutId === preset.id ? 'bg-blue-600/10 ring-1 ring-blue-500/60 shadow-[0_0_0_1px_rgba(59,130,246,0.18),0_0_16px_rgba(59,130,246,0.16)]' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}
                        `}
                  >
                    <div className="flex items-center gap-3">
                      <preset.icon
                        className={`w-4 h-4 ${
                          activeLayoutId === preset.id ? 'text-blue-600 dark:text-blue-500' : 'text-zinc-500 group-hover/item:text-zinc-700 dark:text-gray-500 dark:group-hover/item:text-gray-300'
                        }`}
                      />
                      {editingPresetId === preset.id ? (
                        <input
                          ref={editInputRef}
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename(preset.id);
                            if (e.key === 'Escape') { setEditingPresetId(null); setEditingName(''); }
                          }}
                          onBlur={() => commitRename(preset.id)}
                          className="bg-transparent border-b border-zinc-300 text-sm text-zinc-900 px-1 w-full max-w-[160px] dark:border-zinc-700 dark:text-white"
                        />
                      ) : (
                        <span className={activeLayoutId === preset.id ? 'text-blue-600 dark:text-blue-500 font-medium' : 'text-zinc-700 group-hover/item:text-zinc-900 dark:text-gray-300 dark:group-hover/item:text-white'}>
                          {preset.name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {activeLayoutId === preset.id && !preset.isDefault && <Check className="w-4 h-4 text-blue-600" />}
                      {!preset.isDefault && (
                        <div className="flex items-center gap-0">
                          <button
                            onClick={(e) => startRename(preset.id, preset.name, e)}
                            aria-label={`Rename ${preset.name}`}
                            className={`h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 focus:outline-none transition dark:hover:text-white ${
                              activeLayoutId === preset.id ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100'
                            }`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // guard: only allow deleting user-created layouts
                              if (preset.isDefault) {
                                showToast('Cannot delete built-in layout');
                                return;
                              }
                              setDeleteTargetId(preset.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (preset.isDefault) {
                                    showToast('Cannot delete built-in layout');
                                  return;
                                }
                                setDeleteTargetId(preset.id);
                              }
                            }}
                            aria-label={`Delete ${preset.name}`}
                            className={`h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-red-600 dark:hover:text-red-600 focus:outline-none focus:text-red-500 dark:focus:text-red-400 transition ${
                              activeLayoutId === preset.id ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100'
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </button>
                  {showResetButton && activeLayoutId === preset.id && preset.isDefault && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        requestPresetReset(preset.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          requestPresetReset(preset.id);
                        }
                      }}
                      aria-label={`Reset ${preset.name} to default`}
                      data-tour-target="dashboard-preset-reset"
                      title="Reset to Default"
                      className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-md border border-rose-400/25 bg-rose-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-200/90 transition-colors hover:border-rose-300/45 hover:bg-rose-400/15 hover:text-rose-100"
                    >
                      Reset
                    </button>
                  )}
                </div>
                ))}
              </div>
              {isDev && layoutDebugState && (
                <div className="mt-3 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-2 text-[11px] text-zinc-700 dark:border-zinc-700 dark:bg-zinc-950/40 dark:text-zinc-300">
                  <div className="flex items-center justify-between gap-2 pb-2">
                    <div className="font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-500">Layout Debug</div>
                    <div className="text-zinc-500 dark:text-zinc-500">{String(layoutDebugState.presetId || '')}{layoutDebugState.isPreviewingDefault ? ' previewing' : ''}</div>
                  </div>
                  <div className="space-y-3 max-h-56 overflow-auto pr-1">
                    {renderLayoutSummary('Canonical', layoutDebugState.canonical)}
                    {renderLayoutSummary('Current', layoutDebugState.current)}
                    {renderLayoutSummary('Saved', layoutDebugState.saved)}
                  </div>
                </div>
              )}
            </div>

            <div className="p-2 border-t border-zinc-200 bg-zinc-300 dark:border-zinc-800 dark:bg-zinc-800">
              {!isCreating ? (
                <button
                  onClick={() => setIsCreating(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:text-emerald-600 transition-colors dark:text-zinc-500 dark:hover:text-emerald-400"
                >
                  <Plus className="w-4 h-4" /> Create Custom Layout
                </button>
              ) : (
                <div className="px-2 pb-1 animate-in fade-in slide-in-from-bottom-2">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Layout Name..."
                    className="w-full bg-white border border-zinc-300 rounded px-2 py-1.5 text-xs text-zinc-900 placeholder-zinc-400 focus:border-blue-600 focus:outline-none mb-2 dark:bg-[#0f0f11] dark:border-zinc-700 dark:text-white dark:placeholder-zinc-600"
                    value={newLayoutName}
                    onChange={(e) => setNewLayoutName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreate}
                      className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold py-1.5 rounded transition-colors"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => {
                        setIsCreating(false);
                        setNewLayoutName('');
                      }}
                      className="flex-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 text-xs font-bold py-1.5 rounded transition-colors dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LayoutPresetsMenu;
